import {FormControl, FormGroup, Validators} from '@angular/forms';

import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {switchMap} from 'rxjs/internal/operators/switchMap';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    TradingGroupStatusesEnum,
    TradingGroupBehaviorTypesEnum,
    TradingService,
    TradingGroupInterface,
    TradingGroupModel,
    TradingGroupInputModel,
    TradingGroupStatusTriggerModel,
    TradingPlanService,
    TradingPlanSearchRequestModel,
    TradingPlanSearchResponseModel,
    TradingPlanSearchResponseInterface
} from '@spa/api';

import {InArrayValidator, RequiredValidatorKey} from '@spa/validators';

import {AutocompleteControl, Helper} from '../../../shared/classes';
import {SelectOptionInterface} from '../../../shared/components/select';

import {
    TradingGroupLevelsEnum,
    TradingGroupClientTypesEnum,
    TradingGroupIsDefaultTypesEnum,
    TradingGroupsFormErrorStatesEnum
} from '../enums';

import {
    TradingGroupsControls,
    TradingGroupControlsInterface,
    TradingGroupNameSelector,
    TradingGroupAliasSelector,
    TradingGroupAliasMaxLength,
    TradingGroupStatusSelector,
    TradingGroupFirmLevelSelector,
    TradingAccountIdentifierLength,
    TradingGroupClientTypeSelector,
    TradingGroupAccountClientIdSelector,
    TradingGroupAccountClientNameSelector,
    TradingGroupAccountIdentifierSelector,
    TradingGroupAccountIsDefaultSelector,
    TradingGroupIsDemoSelector,
    TradingGroupPlanTypeSelector,
    TradingGroupOmsGroupIdSelector,
    TradingGroupPlanIdSelector,
    TradingGroupTotalHardBuyingPowerSelector,
    TradingGroupTotalHardBuyingPowerCurrencySelector,
} from '../../../../assets/descriptors';
import { AlertTypesEnum, FeatureEnum } from 'appShared/enums';
import { PanelSettingsGroupsAddMessages } from 'appAssets/messages';
import { AlertsService } from 'appShared/services';
import { environment } from 'appEnv/environment';

import {TradingGroupHardBuyingPowerCurrencyValid, TradingGroupHardBuyingPowerValue} from './trading-group-hard-buying-power';

export class TradingGroupsForm {
    private readonly tradingService: TradingService;
    private readonly tradingPlanService: TradingPlanService;
    private readonly alertsService: AlertsService;
    private plans: TradingPlanSearchResponseInterface[];

    private readonly tradingGroup: TradingGroupModel;

    private tradingGroupStatuses: number[];
    private readonly hardBuyingPowerCurrencyOptions: SelectOptionInterface[];

    public readonly hardBuyingPowerOffered: boolean;
    public form: FormGroup;
    public controls: TradingGroupControlsInterface;

    constructor(
        tradingService: TradingService,
        tradingPlanService: TradingPlanService,
        alertsService: AlertsService,
        tradingGroup: TradingGroupModel = null,
        tradingGroupStatusTriggers: number[] = [],
        hardBuyingPowerOffered: boolean = false,
        hardBuyingPowerCurrencyOptions: SelectOptionInterface[] = []
    ) {
        this.tradingService = tradingService;
        this.tradingPlanService = tradingPlanService;
        this.tradingGroup = tradingGroup;
        this.alertsService = alertsService;
        this.tradingGroupStatuses = tradingGroupStatusTriggers;
        this.hardBuyingPowerOffered = hardBuyingPowerOffered;
        this.hardBuyingPowerCurrencyOptions = hardBuyingPowerCurrencyOptions;
    }

    private getControlValue(name: string): string | number | boolean | number[] {
        switch (name) {
            case TradingGroupNameSelector:
                return this.tradingGroup.name;
            case TradingGroupAliasSelector:
                return this.tradingGroup.alias;
            case TradingGroupStatusSelector:
                return this.tradingGroup.status;
            case TradingGroupClientTypeSelector:
                return !this.tradingGroup.isClientCompany ? TradingGroupClientTypesEnum.Individual :
                    TradingGroupClientTypesEnum.Company;
            case TradingGroupFirmLevelSelector:
                return this.getFirmLevelControlValue();
            case TradingGroupAccountIdentifierSelector:
                return this.tradingGroup.accountIdentifier;
            case TradingGroupAccountIsDefaultSelector:
                return this.getIsDefaultControlValue();
            case TradingGroupAccountClientIdSelector:
                return !this.tradingGroup.isClientCompany ? this.tradingGroup.speciallyForIndividualId :
                    this.tradingGroup.speciallyForCompanyId;
            case TradingGroupAccountClientNameSelector:
                return this.tradingGroup.clientName;
            case TradingGroupIsDemoSelector:
                return this.tradingGroup.isDemo;
            case TradingGroupPlanTypeSelector:
                return this.tradingGroup.tradingPlanTypes;
            case TradingGroupOmsGroupIdSelector:
                return this.tradingGroup.omsGroupId;
            case TradingGroupPlanIdSelector:
                return this.tradingGroup.tradingPlanId;
        }

        return null;
    }

    private getFirmLevelControlValue(): number {
        if (!this.tradingGroup.isClientOnFirmLevel) {
            this.controls.firmLevel.options = this.controls.firmLevel.options
                .filter((option: SelectOptionInterface) => option.value !== TradingGroupLevelsEnum.Firm);

            this.controls.firmLevel.resetValidators();
            this.controls.firmLevel.extendValidators(InArrayValidator([TradingGroupLevelsEnum.Tenant]));

            return TradingGroupLevelsEnum.Tenant;
        }

        this.controls.firmLevel.resetValidators();
        this.controls.firmLevel.extendValidators(InArrayValidator([
            TradingGroupLevelsEnum.Tenant,
            TradingGroupLevelsEnum.Firm
        ]));

        return TradingGroupLevelsEnum.Firm;
    }

    private getIsDefaultControlValue(): number {
        let optionValue = null;

        if (!this.tradingGroup.isDefault) {
            this.controls.clientId.disabled = false;
            this.controls.clientName.disabled = false;

            optionValue = TradingGroupIsDefaultTypesEnum.No;
        } else {
            optionValue = TradingGroupIsDefaultTypesEnum.Yes;
        }

        const radioGroupOption = this.controls.isDefault.getOption(optionValue);

        if (radioGroupOption) {
            radioGroupOption.checked = true;
        }

        return optionValue;
    }

    private getTradingGroupInputModel(): TradingGroupInputModel {
        const clientType = this.form.value[TradingGroupClientTypeSelector];
        const clientId = this.form.value[TradingGroupAccountClientIdSelector];
        const omsGroupId = this.form.value[TradingGroupOmsGroupIdSelector];

        let isDemo = false;
        let planTypes = [];
        let tradingPlanId = null;
        if(Helper.isFeatureEnabled(FeatureEnum.TradingPlans)){
            isDemo = this.form.value[TradingGroupIsDemoSelector];
            planTypes = this.form.value[TradingGroupPlanTypeSelector];
            tradingPlanId = this.form.value[TradingGroupPlanIdSelector];
        }

        let totalHardBuyingPower: number = null;
        let totalHardBuyingPowerCurrency: string = null;
        if (this.hardBuyingPowerOffered) {
            totalHardBuyingPower = TradingGroupHardBuyingPowerValue(this.form.value[TradingGroupTotalHardBuyingPowerSelector]);
            totalHardBuyingPowerCurrency = totalHardBuyingPower === null ? null :
                (this.form.value[TradingGroupTotalHardBuyingPowerCurrencySelector] || null);
        }
        /**
         * Check either the group will be created as default group for individuals or companies.
         */
        const isDefault = (this.form.value[TradingGroupAccountIsDefaultSelector] !== TradingGroupIsDefaultTypesEnum.No);

        let behaviorType = null;
        let speciallyForIndividualId = null;
        let speciallyForCompanyId = null;

        /**
         * Fix type behavior of the group. If the client id is not filled the group is created specially for individual.
         */
        if (clientType !== TradingGroupClientTypesEnum.Company) {
            if (!clientId) {
                behaviorType = TradingGroupBehaviorTypesEnum.ForAllIndividuals;
            } else {
                behaviorType = TradingGroupBehaviorTypesEnum.SpeciallyForIndividual;
                speciallyForIndividualId = clientId;
            }
        } else {
            if (!clientId) {
                behaviorType = TradingGroupBehaviorTypesEnum.ForAllCompanies;
            } else {
                behaviorType = TradingGroupBehaviorTypesEnum.SpeciallyForCompany;
                speciallyForCompanyId = clientId;
            }
        }

        return new TradingGroupInputModel(
            (this.form.value[TradingGroupFirmLevelSelector] !== TradingGroupLevelsEnum.Tenant),
            TradingGroupStatusesEnum.Active,
            this.form.value[TradingGroupNameSelector],
            this.form.value[TradingGroupAliasSelector],
            (this.form.value[TradingGroupAccountIdentifierSelector] as string).toUpperCase(),
            isDefault,
            behaviorType,
            speciallyForIndividualId,
            speciallyForCompanyId,
            null,
            null,
            null,
            isDemo,
            planTypes,
            omsGroupId,
            tradingPlanId,
            totalHardBuyingPower,
            totalHardBuyingPowerCurrency
        );
    }

    private updateTradingGroup(): Observable<ApiResponse<ApiError[] | boolean>> {
        const formTradingGroupStatus = this.form.value[TradingGroupStatusSelector];

        if (this.tradingGroup.status !== formTradingGroupStatus) {
            const triggerInputModel = new TradingGroupStatusTriggerModel(this.tradingGroup.id, formTradingGroupStatus);

            return this.tradingService.updateTradingGroupStatus(triggerInputModel)
                .pipe(
                    switchMap((updateIndividualResponse: ApiResponse<ApiError[] | null>) => {
                        if (updateIndividualResponse.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponseObservable<null>(
                                updateIndividualResponse,
                                TradingGroupsFormErrorStatesEnum.UpdateTradingGroupStatusFailed
                            );
                        }

                        this.tradingGroup.status = formTradingGroupStatus;

                        return this.tradingService.getTradingGroupStatusTriggers(this.tradingGroup.id)
                            .pipe(
                                map((getTriggerResponse: ApiResponse<ApiError[] | number[]>) => {
                                    if (getTriggerResponse.status !== HttpStatusCodesEnum.Ok) {
                                        return Helper.errorsHelper().createErrorResponse<number[]>(
                                            getTriggerResponse,
                                            TradingGroupsFormErrorStatesEnum.GetTradingGroupStatusTriggersFailed
                                        );
                                    }

                                    this.tradingGroupStatuses = getTriggerResponse.data as number[];

                                    return Helper.createSuccessResponse<boolean>(true);
                                })
                            );
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }

    initForm(): void {
        this.form = new FormGroup({});

        this.controls = TradingGroupsControls();
        this.controls.totalHardBuyingPowerCurrency.options = this.hardBuyingPowerCurrencyOptions;

        if (!this.tradingGroup) {
            this.controls.status.value = TradingGroupStatusesEnum.Active;
            this.controls.status.disabled = true;
        } else {
            this.controls.status.value = this.tradingGroup.status;

            this.controls.status.options = this.tradingGroupStatuses.map((trigger: number) => {
                return {
                    label: Helper.textsHelper().getTradingGroupStatusV2Name(trigger),
                    value: trigger
                };
            }).concat({
                label: Helper.textsHelper().getTradingGroupStatusV2Name(this.controls.status.value),
                value: this.controls.status.value
            });

            this.controls.status.setValidators([
                Validators.required,
                InArrayValidator(this.tradingGroupStatuses.concat(this.controls.status.value))
            ]);

            /**
             * Validators are reset, because in edit mode the status can be changed only.
             */
            this.controls.name.resetValidators();
            this.controls.alias.resetValidators();
            this.controls.clientType.resetValidators();
            this.controls.firmLevel.resetValidators();
            this.controls.accountIdentifier.resetValidators();
            this.controls.isDefault.resetValidators();
            this.controls.clientId.resetValidators();
            this.controls.clientName.resetValidators();
            this.controls.omsGroupId.resetValidators();
            this.controls.totalHardBuyingPower.resetValidators();
            if (Helper.isFeatureEnabled(FeatureEnum.TradingPlans)) {
                this.controls.isDemo.resetValidators();
                this.controls.planType.resetValidators();
                this.controls.tradingPlanId.resetValidators();
            }
        }

        Object.keys(this.controls).forEach((key: string) => {
            const control = this.controls[key];

            if (this.tradingGroup) {
                control.value = this.getControlValue(control.name);
            }
            if (control.name === TradingGroupPlanIdSelector) {
                var ctrl = this.controls.tradingPlanId as AutocompleteControl;
                ctrl.asyncOptions = (symbol: string) => {
                    if (!symbol) {
                        return of([]);
                    }
                    const isDemo = this.form.get(TradingGroupIsDemoSelector).value;

                    return this.tradingPlanService
                        .search(new TradingPlanSearchRequestModel(
                            1,
                            1000,
                            TradingPlanSearchResponseModel.getReferenceIdSelector(),
                            true,
                            null,
                            null,
                            null,
                            isDemo,
                            null,
                            symbol,
                            environment.tenant).serialize())
                        .pipe(map(response => {
                            if (response.status !== HttpStatusCodesEnum.Ok) {
                                this.alertsService.addAlert('Error while fetching plans.', AlertTypesEnum.Error);
                                return [];
                            }
                            else {
                                this.plans = response.data as TradingPlanSearchResponseInterface[];

                                if (!this.plans || this.plans.length === 0) {
                                    return [{
                                        label: `${symbol} - The plan is not available.`,
                                        value: symbol
                                    }];
                                }

                                return this.plans.map(p => {
                                    return {
                                        label: p.reference_id,
                                        value: p.id
                                    };
                                });
                            }
                        }));
                };
            }

            this.form.addControl(control.name, new FormControl(control.value, control.validators));
        });
    }

    validateForm(
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((key: string) => {
                const control = this.controls[key];
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    control.errors = Object.keys(formControl.errors).map((errorKey: string) => {
                        if (control.name === TradingGroupAccountIdentifierSelector) {
                            return messageFunction(control.placeholder, TradingAccountIdentifierLength)[errorKey];
                        } else if (control.name === TradingGroupAliasSelector || control.name === TradingGroupOmsGroupIdSelector) {
                            return messageFunction(control.placeholder, TradingGroupAliasMaxLength)[errorKey];
                        } else {
                            return messageFunction(control.placeholder)[errorKey];
                        }
                    });
                }
            });

            return false;
        }

        const clientId = this.form.value[this.controls.clientId.name];
        const firmlevel = this.form.value[this.controls.firmLevel.name];


        if (!this.controls.clientName.disabled && !clientId && firmlevel == TradingGroupLevelsEnum.Firm ) {
            this.controls.clientId.setError(
                messageFunction(this.controls.clientId.placeholder)[RequiredValidatorKey]
            );

            this.controls.clientName.setError(
                messageFunction(this.controls.clientName.placeholder)[RequiredValidatorKey]
            );

            return false;
        }

        const tradingPlanId = this.form.value[this.controls.tradingPlanId.name];
        const planTypes = this.form.value[this.controls.planType.name] as [];
        if(tradingPlanId && planTypes && planTypes.length > 0) {
            this.controls.planType.setError(
                messageFunction(this.controls.planType.placeholder)[PanelSettingsGroupsAddMessages.InvalidPlanConfiguration.key]
            );
            return false;
        }

        if (this.hardBuyingPowerOffered && !TradingGroupHardBuyingPowerCurrencyValid(
            this.form,
            this.controls.totalHardBuyingPower,
            this.controls.totalHardBuyingPowerCurrency,
            messageFunction
        )) {
            return false;
        }

        return true;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        if (!this.tradingGroup) {
            const tradingGroupInputModel = this.getTradingGroupInputModel();

            return this.tradingService.registerGroup(tradingGroupInputModel)
                .pipe(
                    map((registerResponse: ApiResponse<ApiError[] | TradingGroupInterface>) => {
                        if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<TradingGroupInterface>(
                                registerResponse
                            );
                        }

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return this.updateTradingGroup();
    }
}
